import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAppointmentEmails,
  sendAppointmentEmail,
  rearmAppointmentReminder,
  updateAppointment,
} from '../api';
import './appointmentemails.css';

// Which manual sends make sense for an appointment in a given state. A cancelled
// appointment shouldn't offer "send confirmation", and only a scheduled one has a
// reminder worth sending.
const SEND_OPTIONS = [
  { kind: 'confirmation', label: 'Resend confirmation', statuses: ['scheduled', 'arrived'] },
  { kind: 'reminder', label: 'Send reminder now', statuses: ['scheduled'] },
  { kind: 'updated', label: 'Resend details', statuses: ['scheduled', 'arrived'] },
  { kind: 'cancelled', label: 'Resend cancellation', statuses: ['cancelled'] },
];

const STATUS_LABEL = {
  sent: 'Sent',
  failed: 'Failed',
  suppressed: 'Not sent',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Customer-email panel inside the appointment modal: what has been sent, whether it
 * arrived, and the controls to resend or mute.
 *
 * Mounted only for saved appointments — there is nothing to show or send for one that
 * doesn't exist yet.
 */
// optedOut: the customer asked not to be emailed (set on their customer page) — nothing
// goes out, so the send buttons are disabled.
const AppointmentEmails = ({ appointmentId, appointmentStatus, customerEmail, optedOut = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyKind, setBusyKind] = useState(null);

  const [logs, setLogs] = useState([]);
  const [muted, setMuted] = useState(false);
  const [reminderSentAt, setReminderSentAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchAppointmentEmails(appointmentId);
      setLogs(data.results || []);
      setMuted(Boolean(data.emails_muted));
      setReminderSentAt(data.reminder_sent_at || null);
    } catch (err) {
      setLoadError('Could not load the email history.');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  // Deliberately lazy: the history is a support tool, not something staff read on every
  // open, so it costs nothing until the panel is actually expanded.
  useEffect(() => {
    if (expanded && logs.length === 0 && !loadError) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleSend = async (kind, label) => {
    if (!window.confirm(`${label} to ${customerEmail}?`)) return;
    setBusyKind(kind);
    setActionError(null);
    try {
      const data = await sendAppointmentEmail(appointmentId, kind);
      setLogs(data.results || []);
      setReminderSentAt(data.reminder_sent_at || null);
    } catch (err) {
      const body = err.response && err.response.data;
      setActionError((body && (body.detail || body.error)) || 'The email could not be sent.');
      // The attempt is logged server-side even when it fails, so refresh to show it.
      load();
    } finally {
      setBusyKind(null);
    }
  };

  const handleRearm = async () => {
    setBusyKind('rearm');
    setActionError(null);
    try {
      await rearmAppointmentReminder(appointmentId);
      setReminderSentAt(null);
    } catch (err) {
      setActionError('Could not re-arm the reminder.');
    } finally {
      setBusyKind(null);
    }
  };

  const handleToggleMute = async () => {
    const next = !muted;
    if (next && !window.confirm('Mute all automated emails for this appointment?')) return;
    setBusyKind('mute');
    setActionError(null);
    // Optimistic: the toggle should feel instant, and it's reverted on failure below.
    setMuted(next);
    try {
      await updateAppointment(appointmentId, { emails_muted: next, override: true });
    } catch (err) {
      setMuted(!next);
      setActionError('Could not change the mute setting.');
    } finally {
      setBusyKind(null);
    }
  };

  const failedCount = logs.filter((l) => l.status === 'failed').length;

  if (!customerEmail) {
    return (
      <div className="appt-emails">
        <div className="appt-emails-header appt-emails-header-static">
          <span className="appt-emails-title">Customer emails</span>
          <span className="appt-emails-none">No email address on this appointment</span>
        </div>
      </div>
    );
  }

  return (
    <div className="appt-emails">
      <button
        type="button"
        className="appt-emails-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="appt-emails-title">Customer emails</span>
        <span className="appt-emails-summary">
          {optedOut && <span className="appt-emails-badge appt-emails-badge-failed">Customer opted out</span>}
          {!optedOut && muted && <span className="appt-emails-badge appt-emails-badge-muted">Muted</span>}
          {failedCount > 0 && (
            <span className="appt-emails-badge appt-emails-badge-failed">
              {failedCount} failed
            </span>
          )}
          {!muted && !optedOut && failedCount === 0 && logs.length > 0 && (
            <span className="appt-emails-badge appt-emails-badge-ok">{logs.length} sent</span>
          )}
          <span className={`appt-emails-chevron ${expanded ? 'open' : ''}`}>›</span>
        </span>
      </button>

      {expanded && (
        <div className="appt-emails-body">
          <div className="appt-emails-to">
            Sent to <strong>{customerEmail}</strong>
          </div>

          {optedOut && (
            <div className="appt-emails-error">
              This customer asked not to be emailed, so nothing is sent. Turn emails back on
              from their customer page.
            </div>
          )}
          {actionError && <div className="appt-emails-error">{actionError}</div>}
          {loadError && <div className="appt-emails-error">{loadError}</div>}

          {loading ? (
            <div className="appt-emails-loading">Loading…</div>
          ) : (
            <>
              {logs.length === 0 ? (
                <div className="appt-emails-empty">No emails have been sent yet.</div>
              ) : (
                <ul className="appt-emails-list">
                  {logs.map((log) => (
                    <li key={log.id} className={`appt-emails-item appt-emails-item-${log.status}`}>
                      <div className="appt-emails-item-top">
                        <span className="appt-emails-kind">{log.kind_display}</span>
                        <span className={`appt-emails-status appt-emails-status-${log.status}`}>
                          {STATUS_LABEL[log.status] || log.status}
                        </span>
                      </div>
                      <div className="appt-emails-item-meta">
                        {formatWhen(log.created_at)} · {log.to_email}
                      </div>
                      {log.error && <div className="appt-emails-item-error">{log.error}</div>}
                    </li>
                  ))}
                </ul>
              )}

              <div className="appt-emails-actions">
                {SEND_OPTIONS.filter((o) => o.statuses.includes(appointmentStatus)).map((o) => (
                  <button
                    key={o.kind}
                    type="button"
                    className="appt-emails-btn"
                    onClick={() => handleSend(o.kind, o.label)}
                    disabled={muted || optedOut || busyKind !== null}
                    title={optedOut ? 'Customer opted out of email' : muted ? 'Unmute this appointment first' : undefined}
                  >
                    {busyKind === o.kind ? 'Sending…' : o.label}
                  </button>
                ))}

                {appointmentStatus === 'scheduled' && reminderSentAt && (
                  <button
                    type="button"
                    className="appt-emails-btn"
                    onClick={handleRearm}
                    disabled={muted || busyKind !== null}
                    title="Let the nightly job send the 24-hour reminder again"
                  >
                    {busyKind === 'rearm' ? 'Working…' : 'Re-arm 24h reminder'}
                  </button>
                )}
              </div>

              {appointmentStatus === 'scheduled' && (
                <div className="appt-emails-reminder-state">
                  {reminderSentAt
                    ? `24-hour reminder sent ${formatWhen(reminderSentAt)}.`
                    : '24-hour reminder will send automatically the day before.'}
                </div>
              )}

              <label className="appt-emails-mute">
                <input
                  type="checkbox"
                  checked={muted}
                  onChange={handleToggleMute}
                  disabled={busyKind !== null}
                />
                <span>
                  Mute automated emails for this appointment
                  <small>Stops confirmations, updates, cancellations and reminders.</small>
                </span>
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentEmails;
